import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiDelete, apiPost, apiPut } from '../utils/api';
import CreateEpisodeModal from '../components/CreateEpisodeModal';
import EpisodeLoadingState from '../components/EpisodeLoadingState';
import { BsEye, BsPencil, BsPlusCircle, BsChatDots, BsTrash } from 'react-icons/bs';
import AudioPlayer from '../components/AudioPlayer';
import CopyUrlButton from '../components/CopyUrlButton';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import { getEpisodeStatus } from '../utils/taskMonitoring';
import styles from './PodcastDetailsPage.module.css';

const PodcastDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [podcast, setPodcast] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreateEpisodeModalOpen, setIsCreateEpisodeModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [episodeToDelete, setEpisodeToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePodcastModalOpen, setDeletePodcastModalOpen] = useState(false);
  const [deletePodcastLoading, setDeletePodcastLoading] = useState(false);
  const {
    currentlyPlaying,
    audioElement,
    playbackTimes,
    durations,
    currentTimes,
    handlePlayPause,
    handleRewind
  } = useAudioPlayer();
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [showEpisodeDetails, setShowEpisodeDetails] = useState(false);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  // Removed task monitoring state - now using media endpoint structure

  const refreshEpisodes = async () => {
    try {
      const episodesResponse = await apiGet(`/podcast/${id}/episodes/media`);
      if (episodesResponse.ok) {
        const episodesData = await episodesResponse.json();
        setEpisodes(episodesData || []);
      }
    } catch (error) {
      console.error('Error refreshing episodes:', error);
    }
  };

  // Removed task monitoring functions - now using media endpoint structure

  const handleDeleteEpisode = (episode) => {
    setEpisodeToDelete(episode);
    setDeleteModalOpen(true);
  };

  const confirmDeleteEpisode = async () => {
    if (!episodeToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await apiDelete(`/podcast/${id}/episodes/${episodeToDelete.id}`);
      
      if (response.ok) {
        // Refresh episodes list
        await refreshEpisodes();
        setDeleteModalOpen(false);
        setEpisodeToDelete(null);
      } else {
        console.error('Failed to delete episode');
      }
    } catch (error) {
      console.error('Error deleting episode:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setEpisodeToDelete(null);
  };

  const handleDeletePodcast = () => {
    setDeletePodcastModalOpen(true);
  };

  const handleEpisodeCreated = () => {
    refreshEpisodes();
  };


  const confirmDeletePodcast = async () => {
    setDeletePodcastLoading(true);
    try {
      const response = await apiDelete(`/podcast/delete-podcast/${id}`);
      
      if (response.ok) {
        // Navigate back to podcasts list
        navigate('/podcasts');
      } else {
        console.error('Failed to delete podcast');
      }
    } catch (error) {
      console.error('Error deleting podcast:', error);
    } finally {
      setDeletePodcastLoading(false);
    }
  };

  const cancelDeletePodcast = () => {
    setDeletePodcastModalOpen(false);
  };

  // Comments functionality
  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const response = await apiGet(`/podcast/${id}/comments`);
      if (response.ok) {
        const commentsData = await response.json();
        // Sort comments by created_at in descending order (most recent first)
        const sortedComments = (commentsData || []).sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setComments(sortedComments);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleViewComments = () => {
    setCommentsModalOpen(true);
    fetchComments();
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await apiPost(`/podcast/${id}/comments`, {
        content: newComment.trim()
      });
      
      if (response.ok) {
        setNewComment('');
        fetchComments(); // Refresh comments
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditCommentText(comment.content);
  };

  const handleSaveEdit = async (commentId) => {
    if (!editCommentText.trim()) return;
    
    try {
      const response = await apiPut(`/comments/${commentId}`, {
        content: editCommentText.trim()
      });
      
      if (response.ok) {
        setEditingComment(null);
        setEditCommentText('');
        fetchComments(); // Refresh comments
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditCommentText('');
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const response = await apiDelete(`/comments/${commentId}`);
      
      if (response.ok) {
        fetchComments(); // Refresh comments
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };


  useEffect(() => {
    const fetchPodcastDetails = async () => {
      try {
        setLoading(true);
        setError('');
        
        console.log('Fetching podcast details for ID:', id);
        
        // First call: Get podcast info
        const podcastResponse = await apiGet(`/podcast/get-podcast/${id}`);
        console.log('Podcast response status:', podcastResponse.status);
        
        if (podcastResponse.ok) {
          const podcastData = await podcastResponse.json();
          console.log('Podcast data:', podcastData);
          
          // Set podcast info (without episodes)
          const { episodes, ...podcastInfo } = podcastData;
          setPodcast(podcastInfo);
        } else {
          console.error('Failed to fetch podcast:', podcastResponse.status);
          setError('Failed to load podcast details');
        }
        
        // Second call: Get episodes with script and audio data
        const episodesResponse = await apiGet(`/podcast/${id}/episodes/media`);
        console.log('Episodes response status:', episodesResponse.status);
        
        if (episodesResponse.ok) {
          const episodesData = await episodesResponse.json();
          console.log('Episodes data:', episodesData);
          setEpisodes(episodesData || []);
        } else {
          console.warn('Failed to load episodes:', episodesResponse.status);
        }
        
      } catch (error) {
        console.error('Error fetching podcast details:', error);
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPodcastDetails();
  }, [id]);

  // Removed polling cleanup - no longer needed with media endpoint structure

  const handleEditPodcast = () => {
    if (!podcast) return;
    
    // Prepare the podcast data for editing
    const editData = {
      title: podcast.title,
      prompt: podcast.prompt,
      frequency: podcast.frequency,
      duration: podcast.duration,
      hour_of_day: podcast.hour_of_day,
      day_of_week: podcast.day_of_week || '',
      day_of_month: podcast.day_of_month || '',
      publications: podcast.publications || '',
      tone: podcast.tone,
      goals: podcast.goals,
      default_host: podcast.host || 'onyx',
      create_initial_episode: false,
      private: podcast.private || false
    };
    
    // Navigate to create podcast page with edit data
    navigate('/create-podcast', { 
      state: { 
        editMode: true, 
        podcastId: podcast.id, 
        formData: editData 
      } 
    });
  };

  if (loading) {
    return (
      <div className={styles.podcastDetailsPage}>
        <div className="loading">Loading podcast details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.podcastDetailsPage}>
        <div className="error-state">
          <h2>Error Loading Podcast</h2>
          <p>{error}</p>
          <Link to="/podcasts" className="btn btn-primary">Back to Podcasts</Link>
        </div>
      </div>
    );
  }

  if (!podcast) {
    return (
      <div className={styles.podcastDetailsPage}>
        <div className="error-state">
          <h2>Podcast not found</h2>
          <Link to="/podcasts" className="btn btn-primary">Back to Podcasts</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.podcastDetailsPage}>
      <div className={styles.pageHeader}>
        <div className={styles.pageActions}>
          <button 
            className={`${styles.customButton} ${styles.iconButton}`}
            onClick={handleEditPodcast}
            title="Edit Podcast"
          >
            <BsPencil />
          </button>
          <button 
            className={`${styles.customButton} ${styles.iconButton}`}
            onClick={() => setIsCreateEpisodeModalOpen(true)}
            title="Create Episode"
          >
            <BsPlusCircle />
          </button>
          <button 
            className={`${styles.customButton} ${styles.iconButton}`}
            onClick={handleViewComments}
            title="View Comments"
          >
            <BsChatDots />
          </button>
          <button 
            className={`${styles.customTrashButton} ${styles.iconButton}`}
            onClick={handleDeletePodcast}
            title="Delete Podcast"
          >
            <BsTrash />
          </button>
        </div>
      </div>
      
      <div className={styles.podcastHeader}>
        <div className={styles.podcastInfo}>
          <h1>{podcast.title}</h1>
          
          <div className={styles.podcastDetails}>
            <div className={styles.detailRow}>
              <span className={styles.label}>Status:</span>
              <span className={styles.value}>
                <span className={`${styles.status} ${styles[podcast.status]}`}>
                  {podcast.status === 'active' && <span className={styles.checkmark}>✓</span>}
                  {podcast.status === 'active' ? 'Active' : podcast.status}
                </span>
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Visibility:</span>
              <span className={`${styles.value} ${styles.visibility} ${podcast.private ? styles.private : styles.public}`}>
                {podcast.private ? '🔒 Private' : '🌐 Public'}
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Frequency:</span>
              <span className={styles.value}>{podcast.frequency === 'none' ? 'Ad-hoc' : podcast.frequency}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Duration:</span>
              <span className={styles.value}>{podcast.duration} minutes</span>
            </div>
            {podcast.tone && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Tone:</span>
                <span className={styles.value}>{podcast.tone}</span>
              </div>
            )}
            {podcast.prompt && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Prompt:</span>
                <span className={styles.value}>{podcast.prompt}</span>
              </div>
            )}
            {podcast.goals && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Goals:</span>
                <span className={styles.value}>{podcast.goals}</span>
              </div>
            )}
            {podcast.default_host && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Default Voice:</span>
                <span className={styles.value}>{podcast.default_host}</span>
              </div>
            )}
            {podcast.publications && (
              <div className={styles.detailRow}>
                <span className={styles.label}>Publications:</span>
                <span className={styles.value}>{podcast.publications}</span>
              </div>
            )}
            <div className={styles.detailRow}>
              <span className={styles.label}>Created:</span>
              <span className={styles.value}>{new Date(podcast.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles.episodesSection}>
        <h2>Episodes ({episodes.length})</h2>
        {episodes.length === 0 ? (
          <div className={styles.emptyEpisodes}>
            <p>No episodes yet. Episodes will appear here once they're created.</p>
          </div>
        ) : (
          <div className={styles.episodesList}>
            {episodes.map(episode => {
              const status = getEpisodeStatus(episode);
              
              // Show loading state for episodes that are not complete
              if (status.status !== 'complete') {
                return (
                  <EpisodeLoadingState
                    key={episode.id}
                    episode={episode}
                  />
                );
              }
              
              // Show normal episode card for complete episodes
              return (
                <div key={episode.id} className={styles.episodeCard}>
                  <div className={styles.episodeInfo}>
                    <div className={styles.episodeTitleRow}>
                      <CopyUrlButton episode={episode} />
                      <h3>{episode.title}</h3>
                    </div>
                    {episode.notes && <p className={styles.episodeDescription}>{episode.notes}</p>}
                    <div className={styles.episodeMeta}>
                      <span className={styles.episodeNumber}>Episode #{episode.episode_number}</span>
                      {episode.audio_files && episode.audio_files.length > 0 && episode.audio_files[0].duration_seconds && (
                        <span className={styles.duration}>
                          {Math.floor(episode.audio_files[0].duration_seconds / 60)}:{(episode.audio_files[0].duration_seconds % 60).toString().padStart(2, '0')}
                        </span>
                      )}
                      <span className={styles.publishDate}>
                        {new Date(episode.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className={styles.episodeActions}>
                    <AudioPlayer
                      episode={episode}
                      isPlaying={currentlyPlaying === episode.id}
                      onPlayPause={handlePlayPause}
                      onRewind={handleRewind}
                      currentTime={currentTimes[episode.id] || 0}
                      duration={durations[episode.id] || 0}
                      playbackTime={playbackTimes[episode.id] || 0}
                      onViewDetails={() => {
                        setSelectedEpisode(episode);
                        setShowEpisodeDetails(true);
                      }}
                      onDeleteEpisode={handleDeleteEpisode}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <CreateEpisodeModal
        isOpen={isCreateEpisodeModalOpen}
        onClose={() => setIsCreateEpisodeModalOpen(false)}
        podcastId={id}
        defaultHost={podcast?.default_host}
        onEpisodeCreated={handleEpisodeCreated}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className={styles.modalOverlay} onClick={cancelDelete}>
          <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Delete Episode</h3>
              <button 
                className={styles.closeButton}
                onClick={cancelDelete}
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <p>Are you sure you want to delete this episode?</p>
              <div className={styles.episodePreview}>
                <strong>{episodeToDelete?.title}</strong>
                {episodeToDelete?.notes && (
                  <p className={styles.episodeDescription}>{episodeToDelete.notes}</p>
                )}
              </div>
              <p className={styles.warningText}>
                This action cannot be undone. All associated content (script, audio) will be permanently deleted.
              </p>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={cancelDelete}
                className="btn btn-secondary"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteEpisode}
                className="btn btn-danger"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Episode'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Podcast Confirmation Modal */}
      {deletePodcastModalOpen && (
        <div className={styles.modalOverlay} onClick={cancelDeletePodcast}>
          <div className={styles.deleteModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Delete Podcast</h3>
              <button 
                className={styles.closeButton}
                onClick={cancelDeletePodcast}
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              <p>Are you sure you want to delete this podcast?</p>
              <div className={styles.episodePreview}>
                <strong>{podcast?.title}</strong>
                <p className={styles.episodeDescription}>
                  This will permanently delete the podcast and all {episodes.length} associated episodes, scripts, and audio files.
                </p>
              </div>
              <p className={styles.warningText}>
                This action cannot be undone. All content will be permanently deleted.
              </p>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={cancelDeletePodcast}
                className="btn btn-secondary"
                disabled={deletePodcastLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeletePodcast}
                className="btn btn-danger"
                disabled={deletePodcastLoading}
              >
                {deletePodcastLoading ? 'Deleting...' : 'Delete Podcast'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {commentsModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setCommentsModalOpen(false)}>
          <div className={styles.commentsModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Comments ({comments.length})</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setCommentsModalOpen(false)}
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              {/* Add Comment Form */}
              <div className={styles.addCommentForm}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className={styles.commentTextarea}
                  rows="3"
                />
                <button
                  onClick={handleAddComment}
                  className="btn btn-primary"
                  disabled={!newComment.trim()}
                >
                  Add Comment
                </button>
              </div>

              {/* Comments List */}
              <div className={styles.commentsList}>
                {commentsLoading ? (
                  <div className={styles.loading}>Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div className={styles.emptyComments}>
                    <p>No comments yet. Be the first to comment!</p>
                  </div>
                ) : (
                  comments.map(comment => (
                    <div key={comment.id} className={styles.commentCard}>
                      <div className={styles.commentHeader}>
                        <div className={styles.commentAuthor}>
                          <strong>{comment.user?.first_name} {comment.user?.last_name}</strong>
                          <span className={styles.commentDate}>
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {comment.user_id === user?.id && (
                          <div className={styles.commentActions}>
                            {editingComment === comment.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveEdit(comment.id)}
                                  className="btn btn-sm btn-primary"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="btn btn-sm btn-secondary"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditComment(comment)}
                                  className="btn btn-sm btn-secondary"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="btn btn-sm btn-danger"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={styles.commentContent}>
                        {editingComment === comment.id ? (
                          <textarea
                            value={editCommentText}
                            onChange={(e) => setEditCommentText(e.target.value)}
                            className={styles.editCommentTextarea}
                            rows="3"
                          />
                        ) : (
                          <p>{comment.content}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Episode Details Modal */}
      {showEpisodeDetails && selectedEpisode && (
        <div className={styles.episodeDetailsModal}>
          <div className={styles.episodeDetailsContent}>
            <div className={styles.episodeDetailsHeader}>
              <h3>Episode Details</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowEpisodeDetails(false)}
              >
                ×
              </button>
            </div>
            
            <div className={styles.episodeDetailsBody}>
              <div className={styles.episodeInfo}>
                <h4>{selectedEpisode.title}</h4>
                <p><strong>Episode #{selectedEpisode.episode_number}</strong></p>
                <p><strong>Created:</strong> {new Date(selectedEpisode.created_at).toLocaleDateString()}</p>
                {selectedEpisode.notes && (
                  <div className={styles.notesSection}>
                    <h5>Notes</h5>
                    <p>{selectedEpisode.notes}</p>
                  </div>
                )}
              </div>

              {selectedEpisode.script && (
                <div className={styles.scriptSection}>
                  <h5>Script Content</h5>
                  <div className={styles.scriptContent}>
                    {selectedEpisode.script.script_content}
                  </div>
                  <div className={styles.scriptStats}>
                    <span><strong>Word Count:</strong> {selectedEpisode.script.word_count}</span>
                  </div>
                </div>
              )}

              {selectedEpisode.script && selectedEpisode.script.search_history && (
                <div className={styles.searchSection}>
                  <h5>Search Queries</h5>
                  <div className={styles.searchQueries}>
                    {JSON.parse(selectedEpisode.script.search_history).map((search, index) => (
                      <div key={index} className={styles.searchQuery}>
                        <span className={styles.searchQueryText}>"{search.query}"</span>
                        <span className={styles.searchTimestamp}>
                          {new Date(search.timestamp).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEpisode.audio_files && selectedEpisode.audio_files.length > 0 && (
                <div className={styles.audioSection}>
                  <h5>Audio Files</h5>
                  {selectedEpisode.audio_files.map((audio, index) => (
                    <div key={index} className={styles.audioFile}>
                      <p><strong>Voice:</strong> {audio.voice}</p>
                      <p><strong>Model:</strong> {audio.model}</p>
                      <p><strong>Duration:</strong> {Math.floor(audio.duration_seconds / 60)}:{(audio.duration_seconds % 60).toString().padStart(2, '0')}</p>
                      <p><strong>File Size:</strong> {(audio.file_size / 1024 / 1024).toFixed(2)} MB</p>
                      <p><strong>Generated:</strong> {new Date(audio.generated_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastDetailsPage;
